import {
  ArrowRight,
  CheckCircle2,
  Crosshair,
  ExternalLink,
  FlaskConical,
  LineChart,
  Mail,
  ShieldCheck,
  Waves,
  Zap,
} from "lucide-react";

const products = [
  {
    name: "HireProxy",
    url: "https://hireproxy.app",
    status: "Active",
    logo: "/assets/hireproxy-logo.png",
    logoShape: "wide",
    logoAlt: "HireProxy logo",
    title: "AI interview practice that gets candidates ready.",
    description:
      "Voice-led interview practice with job-specific questions, adaptive follow-ups, coaching, job-fit analysis, and career story refinement.",
    bullets: [
      "Job-fit analysis",
      "Voice interview practice",
      "Adaptive coaching",
      "Career story refinement",
    ],
  },
  {
    name: "LAX Commit Coach",
    url: "https://laxcommitcoach.com",
    status: "Active",
    logo: "/assets/laxcommitcoach-logo.png",
    logoShape: "square",
    logoAlt: "LAX Commit Coach logo",
    title: "Recruiting guidance for lacrosse families.",
    description:
      "A recruiting companion for exploring NCAA programs, comparing school fit, and turning recruiting uncertainty into a clearer plan.",
    bullets: [
      "400+ NCAA programs",
      "Cost and school data",
      "AI-powered recommendations",
      "Recruiting decision support",
    ],
  },
];

const principles = [
  {
    icon: Crosshair,
    title: "Focused by design",
    body: "Each product starts with a specific decision, a specific user, and a workflow that should feel lighter when the app works.",
  },
  {
    icon: ShieldCheck,
    title: "Trust first",
    body: "Privacy, transparency, and security are product requirements, especially when people bring resumes, recruiting goals, or personal context.",
  },
  {
    icon: Zap,
    title: "Practical AI",
    body: "AI should sharpen judgment and reduce busywork. Human review and clear next steps stay central.",
  },
  {
    icon: LineChart,
    title: "Measured shipping",
    body: "We ship narrow product improvements, dogfood them in real workflows, and keep raising the quality bar.",
  },
];

const entityFacts = [
  {
    question: "Who is the founder?",
    answer:
      "Robert Parker, also known as Rob Parker, is the founder of Broadlake Technologies LLC and the creator behind HireProxy.",
  },
  {
    question: "What does Broadlake operate?",
    answer:
      "Broadlake Technologies LLC operates HireProxy and LAX Commit Coach, with additional focused AI and web applications in development.",
  },
];

function Header() {
  return (
    <header className="site-header">
      <a className="brand" href="#top" aria-label="Broadlake Technologies home">
        <img src="/assets/broadlake-logo.jpeg" alt="" className="brand-mark" />
        <span>
          <strong>Broadlake</strong>
          <small>Technologies</small>
        </span>
      </a>

      <nav className="site-nav" aria-label="Main navigation">
        <a href="#work">Work</a>
        <a href="#founder">Founder</a>
        <a href="#principles">Principles</a>
        <a href="#contact">Contact</a>
      </nav>

      <a className="header-cta" href="#work">
        <span className="cta-wide">Explore the apps</span>
        <span className="cta-short">Apps</span>
        <ArrowRight aria-hidden="true" size={18} />
      </a>
    </header>
  );
}

function ProductRow({ product }) {
  return (
    <article className="product-row">
      <a
        className={`product-logo-wrap product-logo-wrap-${product.logoShape}`}
        href={product.url}
        target="_blank"
        rel="noreferrer"
        aria-label={`Visit ${product.name}`}
      >
        <img src={product.logo} alt={product.logoAlt} className="product-logo" />
      </a>

      <div className="product-copy">
        <p className="status">{product.status}</p>
        <h3>{product.name}</h3>
        <p className="product-title">{product.title}</p>
        <p>{product.description}</p>
      </div>

      <div className="product-proof" aria-label={`${product.name} highlights`}>
        <ul>
          {product.bullets.map((bullet) => (
            <li key={bullet}>
              <CheckCircle2 aria-hidden="true" size={18} />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
        <a href={product.url} target="_blank" rel="noreferrer">
          Visit {product.name}
          <ExternalLink aria-hidden="true" size={17} />
        </a>
      </div>
    </article>
  );
}

function Principle({ item }) {
  const Icon = item.icon;

  return (
    <article className="principle">
      <Icon aria-hidden="true" size={31} />
      <h3>{item.title}</h3>
      <p>{item.body}</p>
    </article>
  );
}

function EntityFact({ item }) {
  return (
    <article className="entity-fact">
      <h3>{item.question}</h3>
      <p>{item.answer}</p>
    </article>
  );
}

export default function App() {
  return (
    <>
      <Header />

      <main id="top">
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero-inner">
            <div className="hero-copy">
              <h1 id="hero-title">Broadlake Technologies</h1>
              <div className="hero-rule" aria-hidden="true" />
              <p className="hero-lede">Practical AI products for high-stakes decisions.</p>
              <p className="hero-body">
                We build focused web applications that turn messy moments into clear
                next steps, from interview practice to athletic recruiting.
              </p>
              <div className="hero-actions">
                <a className="primary-action" href="#work">
                  Explore the apps
                  <ArrowRight aria-hidden="true" size={20} />
                </a>
                <a className="secondary-action" href="#principles">
                  How we build
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="work-section" id="work" aria-labelledby="work-title">
          <div className="section-shell">
            <div className="section-heading">
              <p className="section-label">Our work</p>
              <h2 id="work-title">Focused products. Real workflows.</h2>
              <p>
                Broadlake ships its own products in the open: narrow tools for people
                making important career, recruiting, and performance decisions.
              </p>
            </div>

            <div className="product-list">
              {products.map((product) => (
                <ProductRow key={product.name} product={product} />
              ))}

              <article className="product-row product-row-subtle">
                <div className="product-logo-wrap more-wrap" aria-hidden="true">
                  <FlaskConical size={42} />
                </div>
                <div className="product-copy">
                  <p className="status">In development</p>
                  <h3>More products in the works</h3>
                  <p className="product-title">
                    Additional focused apps are moving through validation.
                  </p>
                  <p>
                    The pattern stays consistent: identify a stressful decision,
                    build a practical workflow around it, then improve it through
                    real use.
                  </p>
                </div>
                <div className="product-proof">
                  <p>
                    Parent-company context, product updates, and new launches will
                    live here as the portfolio grows.
                  </p>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="founder-section" id="founder" aria-labelledby="founder-title">
          <div className="section-shell founder-layout">
            <div className="founder-media">
              <img
                src="/assets/rob-parker-headshot.jpg"
                alt="Robert Parker, founder of Broadlake Technologies LLC"
                className="founder-photo"
              />
            </div>

            <div className="founder-copy">
              <p className="section-label">Official entity</p>
              <h2 id="founder-title">Broadlake Technologies LLC is Robert Parker's software product company.</h2>
              <p>
                Broadlake Technologies LLC is the US-based company behind HireProxy
                and LAX Commit Coach. It builds focused AI and web applications for
                career, recruiting, and decision-support workflows.
              </p>
              <div className="founder-links" aria-label="Robert Parker profile links">
                <a href="https://www.linkedin.com/in/robrparker" target="_blank" rel="noreferrer">
                  Robert Parker on LinkedIn
                  <ExternalLink aria-hidden="true" size={17} />
                </a>
                <a href="https://rob-parker.hireproxy.app" target="_blank" rel="noreferrer">
                  Rob Parker AI Career Agent
                  <ExternalLink aria-hidden="true" size={17} />
                </a>
              </div>
            </div>
          </div>

          <div className="section-shell entity-facts-shell" aria-label="Broadlake Technologies entity facts">
            <div className="entity-facts">
              {entityFacts.map((item) => (
                <EntityFact key={item.question} item={item} />
              ))}
            </div>
          </div>
        </section>

        <section className="principles-section" id="principles" aria-labelledby="principles-title">
          <div className="section-shell">
            <div className="section-heading light-heading">
              <p className="section-label">Our principles</p>
              <h2 id="principles-title">Build with purpose. Ship with discipline.</h2>
            </div>

            <div className="principles-grid">
              {principles.map((item) => (
                <Principle key={item.title} item={item} />
              ))}
            </div>
          </div>
        </section>

        <section className="contact-section" id="contact" aria-labelledby="contact-title">
          <div className="contact-panel">
            <div>
              <h2 id="contact-title">Contact</h2>
            </div>

            <div className="contact-actions">
              <a href="mailto:hello@broadlaketechnologies.com">
                Broadlake Technologies
                <Mail aria-hidden="true" size={17} />
              </a>
              <a href="mailto:support@hireproxy.app">
                HireProxy
                <Mail aria-hidden="true" size={17} />
              </a>
              <a href="mailto:support@laxcommitcoach.com">
                LAX Commit Coach
                <Mail aria-hidden="true" size={17} />
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div>
          <Waves aria-hidden="true" size={20} />
          <span>Broadlake Technologies LLC</span>
        </div>
        <span>Building focused AI and web products.</span>
      </footer>
    </>
  );
}
